// API client for Xpertpests backend (FastAPI under /api/*)
import axios from 'axios';

// Empty means "same origin", which is how this is deployed: the FastAPI app
// serves the built frontend, so /api is relative. Without the ?? '' an unset
// REACT_APP_BACKEND_URL builds a bundle that requests "undefined/api/..." —
// those resolve to the SPA's own index.html, so every call returns a page of
// HTML with a 200 and the app crashes on the first reduce() over it. A build
// that is wrong this way looks fine until it is opened.
const BASE = process.env.REACT_APP_BACKEND_URL ?? '';
export const API = `${BASE}/api`;

const http = axios.create({ baseURL: API, timeout: 20000 });

// The AI routes are the exception to that 20s. A generation takes as long as it
// takes — the model reasons before it answers, and these prompts carry the whole
// company knowledge base — so 20s cut every assistant off mid-answer and showed a
// generic failure. The server's own limit (backend/ai_service.py) is lower than
// this, so the server gives up first and can say why.
const AI_TIMEOUT = { timeout: 180000 };

http.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem('xp_token');
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

// If the server says our session is no longer valid (401), clear it and send
// the user to the login screen — never silently fall through to stale/demo data.
// The public pay-by-link flow is unauthenticated, so it is skipped here.
http.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || '';
    const isPublic = url.startsWith('/pay') || url.startsWith('/public');
    if (status === 401 && localStorage.getItem('xp_token') && !isPublic) {
      localStorage.removeItem('xp_token');
      localStorage.removeItem('pp_user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?expired=1';
      }
    }
    return Promise.reject(err);
  }
);

export const auth = {
  login: (email, password, role) => http.post('/auth/login', { email, password, role }).then(r => r.data),
  me: () => http.get('/auth/me').then(r => r.data),
  changePassword: (currentPassword, newPassword) => http.post('/auth/change-password', { currentPassword, newPassword }).then(r => r.data),
};

export const users = {
  list: () => http.get('/users').then(r => r.data),
  create: (body) => http.post('/users', body).then(r => r.data),
  update: (email, body) => http.put(`/users/${encodeURIComponent(email)}`, body).then(r => r.data),
  setPassword: (email, password) => http.post(`/users/${encodeURIComponent(email)}/password`, { password }).then(r => r.data),
  remove: (email) => http.delete(`/users/${encodeURIComponent(email)}`).then(r => r.data),
};

export const email = {
  status: () => http.get('/email/status').then(r => r.data),
  sendInvoice: (id) => http.post(`/invoices/${id}/send`, { originUrl: window.location.origin }).then(r => r.data),
  sendEstimate: (id) => http.post(`/estimates/${id}/send`, { originUrl: window.location.origin }).then(r => r.data),
};

export const jobApi = {
  complete: (id, body) => http.post(`/workorders/${id}/complete`, body || {}).then(r => r.data),
  // The electronic service report: what was done and what was applied. Filing it
  // is what releases the invoice the completion held back, so it can also be
  // filed later for a job already closed out.
  serviceReport: (id, body) => http.post(`/workorders/${id}/service-report`, body).then(r => r.data),
};

const crud = (name) => ({
  list: () => http.get(`/${name}`).then(r => r.data),
  get: (id) => http.get(`/${name}/${id}`).then(r => r.data),
  create: (body) => http.post(`/${name}`, body).then(r => r.data),
  update: (id, body) => http.put(`/${name}/${id}`, body).then(r => r.data),
  remove: (id) => http.delete(`/${name}/${id}`).then(r => r.data),
  bulk: (items) => http.post(`/${name}/bulk`, items).then(r => r.data),
});

export const apiCustomers = crud('customers');
export const apiVendors = crud('vendors');
export const apiProducts = crud('products');
export const apiInvoices = crud('invoices');
export const apiEstimates = crud('estimates');
export const apiExpenses = crud('expenses');
export const apiBills = crud('bills');
export const apiAccounts = crud('accounts');
export const apiTransactions = crud('transactions');
export const apiLeads = crud('leads');
export const apiContracts = crud('contracts');
export const apiInventory = crud('inventory');
export const apiMessages = crud('messages');
export const apiWorkorders = crud('workorders');
export const apiForms = crud('formsubmissions');
export const apiServicePlans = crud('serviceplans');

export const servicePlans = {
  run: (date) => http.post('/service-plans/run', { date }).then(r => r.data),
};

export const pesticide = {
  list: (start, end) => http.get('/pesticide-applications', { params: { start, end } }).then(r => r.data),
  create: (body) => http.post('/pesticide-applications', body).then(r => r.data),
  remove: (id) => http.delete(`/pesticide-applications/${id}`).then(r => r.data),
  report: (params) => http.get('/pesticide-applications/report', { params: params || {} }).then(r => r.data),
};

export const devices = {
  list: (customerId) => http.get('/devices', { params: customerId ? { customerId } : {} }).then(r => r.data),
  create: (body) => http.post('/devices', body).then(r => r.data),
  update: (id, body) => http.patch(`/devices/${id}`, body).then(r => r.data),
  remove: (id) => http.delete(`/devices/${id}`).then(r => r.data),
  inspect: (body) => http.post('/device-inspections', body).then(r => r.data),
  inspections: (params) => http.get('/device-inspections', { params: params || {} }).then(r => r.data),
  report: (params) => http.get('/devices/report', { params: params || {} }).then(r => r.data),
};

export const reports = {
  summary: () => http.get('/reports/summary').then(r => r.data),
  // The Business Briefing panel has called this since it was written; it was
  // never defined here, so every run threw "not a function" before a request
  // was made. It is AI-backed, hence the long timeout.
  businessSummary: (period) => http.get('/reports/business-summary', {
    params: period ? { period } : {}, ...AI_TIMEOUT,
  }).then(r => r.data),
};

export const messaging = {
  contacts: () => http.get('/messaging/contacts').then(r => r.data),
  conversations: () => http.get('/messaging/conversations').then(r => r.data),
  start: (withEmail) => http.post('/messaging/conversations', { withEmail }).then(r => r.data),
  messages: (cid) => http.get(`/messaging/conversations/${cid}/messages`).then(r => r.data),
  send: (cid, text) => http.post(`/messaging/conversations/${cid}/messages`, { text }).then(r => r.data),
  unreadCount: () => http.get('/messaging/unread-count').then(r => r.data),
};

export const ce = {
  courses: () => http.get('/ce/courses').then(r => r.data),
  addCourse: (b) => http.post('/ce/courses', b).then(r => r.data),
  updateCourse: (id, b) => http.put(`/ce/courses/${id}`, b).then(r => r.data),
  deleteCourse: (id) => http.delete(`/ce/courses/${id}`).then(r => r.data),
  complete: (id) => http.post(`/ce/courses/${id}/complete`).then(r => r.data),
  myProgress: () => http.get('/ce/my-progress').then(r => r.data),
  compliance: () => http.get('/ce/compliance').then(r => r.data),
  setProfile: (b) => http.put('/ce/profile', b).then(r => r.data),
};

export const team = {
  technicians: () => http.get('/team/technicians').then(r => r.data),
  reportLocation: (lat, lng) => http.post('/team/location', { lat, lng }).then(r => r.data),
  locations: () => http.get('/team/locations').then(r => r.data),
};

export const geo = {
  batch: (addresses) => http.post('/geocode/batch', { addresses }).then(r => r.data),
};

export const payments = {
  createSession: (invoiceId) => http.post('/payments/checkout/session', { invoiceId, originUrl: window.location.origin }).then(r => r.data),
  status: (sid) => http.get(`/payments/checkout/status/${sid}`).then(r => r.data),
  // Public pay-from-email flow (no auth required)
  publicInvoice: (invoiceId, token) => http.get(`/pay/${invoiceId}`, { params: { t: token } }).then(r => r.data),
  publicCheckout: (invoiceId, token) => http.post(`/pay/${invoiceId}/checkout`, { originUrl: window.location.origin, token }).then(r => r.data),
  publicStatus: (sid) => http.get(`/pay/status/${sid}`).then(r => r.data),
};

export const settingsApi = {
  get: () => http.get('/settings').then(r => r.data),
  update: (body) => http.put('/settings', body).then(r => r.data),
};

export const adminApi = {
  seedDemo: () => http.post('/admin/seed-demo').then(r => r.data),
  bulkSeedCsv: () => http.post('/admin/bulk-seed-from-csv').then(r => r.data),
  config: () => http.get('/config').then(r => r.data),
};

export const comms = {
  summary: () => http.get('/comms/summary').then(r => r.data),
  log: (channel) => http.get('/comms/log', { params: channel ? { channel } : {} }).then(r => r.data),
  telegramConfig: () => http.get('/comms/telegram/config').then(r => r.data),
  saveTelegram: (body) => http.post('/comms/telegram/config', body).then(r => r.data),
  sendTelegram: (text, chatId) => http.post('/comms/telegram/send', { text, chatId }).then(r => r.data),
  smsConfig: () => http.get('/comms/sms/config').then(r => r.data),
  saveSms: (body) => http.post('/comms/sms/config', body).then(r => r.data),
  sendSms: (to, text) => http.post('/comms/sms/send', { to, text }).then(r => r.data),
};

export const audit = {
  list: (params) => http.get('/audit', { params: params || {} }).then(r => r.data),
  summary: () => http.get('/audit/summary').then(r => r.data),
  pack: (params) => http.get('/audit-pack', { params: params || {} }).then(r => r.data),
};

export const publicApi = {
  createLead: (body) => http.post('/public/leads', body).then(r => r.data),
};

// Cheque scanning. Two calls on purpose: `scan` reads and proposes and writes
// nothing, `apply` records what a person has looked at and agreed to. A scan
// that proposed the wrong customer has to be cheap to throw away.
export const checks = {
  scan: (attachmentIds) => http.post('/payments/scan-check', { attachmentIds }, { timeout: 180000 }).then(r => r.data),
  apply: (body) => http.post('/payments/apply-check', body).then(r => r.data),
};

// Scanned paper service reports. Same two-step shape as cheques: `scan` reads
// and proposes and writes nothing, `apply` files what a person has approved.
export const serviceReports = {
  scan: (attachmentIds) => http.post('/servicereports/scan', { attachmentIds }, { timeout: 180000 }).then(r => r.data),
  // A stack of sheets, one photograph each. Every sheet is a separate read, so
  // the wait grows with the pile — hence the longer ceiling.
  scanBatch: (attachmentIds) => http.post('/servicereports/scan-batch', { attachmentIds }, { timeout: 900000 }).then(r => r.data),
  apply: (body) => http.post('/servicereports/apply', body).then(r => r.data),
};

// Calendar. Two directions: a feed an external calendar subscribes to, and an
// import that reads an .ics file back into the schedule. The import is a dry
// run unless told otherwise, so the office sees what it would do first.
export const calendar = {
  feedUrl: () => http.get('/calendar/feed-url').then(r => r.data),
  rotateFeed: () => http.post('/calendar/feed-url/rotate').then(r => r.data),
  importIcs: (body) => http.post('/calendar/import', body, { timeout: 120000 }).then(r => r.data),
  // The connected calendar, synced by URL rather than by uploading a file.
  getSource: () => http.get('/calendar/source').then(r => r.data),
  setSource: (url, autoSync) => http.put('/calendar/source', { url, autoSync }, { timeout: 60000 }).then(r => r.data),
  clearSource: () => http.delete('/calendar/source').then(r => r.data),
  sync: (dryRun) => http.post('/calendar/sync', { dryRun }, { timeout: 120000 }).then(r => r.data),
};

// Attachments. Files are stored server-side and referenced by id, so a photo a
// technician takes survives the tab being closed — which the old
// URL.createObjectURL() version did not.
//
// Downloads go through axios rather than a plain <img src>, because the API
// wants an Authorization header and an <img> tag cannot send one. Callers get
// an object URL and are responsible for revoking it.
export const uploads = {
  upload: (files, { kind = 'other', linkedType = 'none', linkedId = '', note = '', onProgress } = {}) => {
    const fd = new FormData();
    (Array.isArray(files) ? files : [files]).forEach((f) => fd.append('files', f, f.name || 'upload'));
    fd.append('kind', kind);
    fd.append('linkedType', linkedType);
    fd.append('linkedId', linkedId);
    fd.append('note', note);
    return http.post('/uploads', fd, {
      // Photos over a truck's LTE connection take much longer than a JSON call.
      timeout: 120000,
      onUploadProgress: onProgress ? (e) => onProgress(e.total ? e.loaded / e.total : 0) : undefined,
    }).then(r => r.data);
  },
  list: (params) => http.get('/uploads', { params: params || {} }).then(r => r.data),
  blobUrl: (id) => http.get(`/uploads/${id}`, { responseType: 'blob' }).then(r => URL.createObjectURL(r.data)),
  remove: (id) => http.delete(`/uploads/${id}`).then(r => r.data),
};

export const automations = {
  config: () => http.get('/automations/config').then(r => r.data),
  save: (body) => http.post('/automations/config', body).then(r => r.data),
  runReminders: (date) => http.post('/automations/run-reminders', { date }).then(r => r.data),
  runToday: () => http.post('/automations/run-today').then(r => r.data),
  catalog: () => http.get('/automations/catalog').then(r => r.data),
  runOne: (key) => http.post(`/automations/${key}/run`).then(r => r.data),
  runDue: () => http.post('/automations/run-due').then(r => r.data),
  notifyWorkorder: (id, type) => http.post(`/workorders/${id}/notify`, { type }).then(r => r.data),
};

export const pricing = {
  rules: () => http.get('/pricing-rules').then(r => r.data),
  updateRule: (id, body) => http.put(`/pricing-rules/${id}`, body).then(r => r.data),
  suggest: (body) => http.post('/pricing/suggest', body).then(r => r.data),
};

export const invoiceActions = {
  sendBulk: (body) => http.post('/invoices/send-bulk', { originUrl: window.location.origin, ...body }).then(r => r.data),
  bulkUpdate: (ids, status) => http.post('/invoices/bulk-update', { ids, status }).then(r => r.data),
  duplicate: (id) => http.post(`/invoices/${id}/duplicate`).then(r => r.data),
};

export const scheduleTracker = {
  get: (withinDays = 7) => http.get('/schedule/tracker', { params: { withinDays } }).then(r => r.data),
  autoBook: (body) => http.post('/schedule/auto-book', body).then(r => r.data),
  autoBookAll: (withinDays = 7) => http.post('/schedule/auto-book-all', null, { params: { withinDays } }).then(r => r.data),
};

export const orgs = {
  current: () => http.get('/orgs/current').then(r => r.data),
  rename: (name) => http.put('/orgs/current', { name }).then(r => r.data),
  list: () => http.get('/orgs').then(r => r.data),
  create: (body) => http.post('/orgs', body).then(r => r.data),
  setActive: (id, active) => http.patch(`/orgs/${id}/active`, { active }).then(r => r.data),
};


export const pesticideProducts = {
  list: (params) => http.get('/pesticide-products', { params: params || {} }).then(r => r.data),
  categories: () => http.get('/pesticide-products/categories').then(r => r.data),
  vocab: () => http.get('/pesticide-vocab').then(r => r.data),
  create: (body) => http.post('/pesticide-products', body).then(r => r.data),
  update: (id, body) => http.put(`/pesticide-products/${id}`, body).then(r => r.data),
  retire: (id) => http.delete(`/pesticide-products/${id}`).then(r => r.data),
  reseed: () => http.post('/pesticide-products/reseed').then(r => r.data),
};


export const workorderStatuses = {
  list: () => http.get('/workorder-statuses').then(r => r.data),
  set: (id, status, note) => http.post(`/workorders/${id}/status`, { status, note }).then(r => r.data),
  setMany: (ids, status, note) =>
    http.post('/workorders/bulk-status', { ids, status, note }).then(r => r.data),
};

// The GorillaDesk importers. Both plan first: nothing is written until the same
// text is sent again with dryRun false, which is why the screen makes you look
// at the counts before it will let you commit.
export const gdImport = {
  invoices: (csv, dryRun = true) =>
    http.post('/admin/import/gorilladesk-invoices', { csv, dryRun }).then(r => r.data),
  payments: (csv, dryRun = true) =>
    http.post('/admin/import/gorilladesk-payments', { csv, dryRun }).then(r => r.data),
};

// Billing a batch of visits in one go. The server skips a job that already has
// an invoice and reports what it left out, so the screen can say so rather than
// claim a number it did not write.
export const workorderActions = {
  bulkInvoice: (ids, includeZeroAmount = false) =>
    http.post('/workorders/bulk-invoice', { ids, includeZeroAmount }).then(r => r.data),
};


// --- merged from the July line (AI, banking, knowledge, EOD, payments) ---
export const apiPayments = crud('payments');

export const apiPestLibrary = crud('pestlibrary');

export const apiKnowledge = crud('knowledge');

export const apiBankRules = crud('bankrules');

export const banking = {
  plaidStatus: () => http.get('/banking/plaid/status').then(r => r.data),
  linkToken: () => http.post('/banking/plaid/link-token').then(r => r.data),
  exchange: (public_token, institution) => http.post('/banking/plaid/exchange', { public_token, institution }).then(r => r.data),
  sync: () => http.post('/banking/plaid/sync').then(r => r.data),
  connectDemo: () => http.post('/banking/connect-demo').then(r => r.data),
  applyRules: () => http.post('/banking/apply-rules').then(r => r.data),
};

export const news = {
  snapshot: () => http.get('/news/snapshot').then(r => r.data),
};

export const eod = {
  report: (date) => http.get('/reports/end-of-day', { params: date ? { date } : {} }).then(r => r.data),
  clockOut: () => http.post('/reports/end-of-day/clock-out').then(r => r.data),
  clockOuts: (date) => http.get('/reports/clock-outs', { params: date ? { date } : {} }).then(r => r.data),
};

export const ai = {
  status: () => http.get('/ai/status').then(r => r.data),
  draft: (body) => http.post('/ai/draft', body, AI_TIMEOUT).then(r => r.data),
  summarize: (body) => http.post('/ai/summarize', body, AI_TIMEOUT).then(r => r.data),
  assistant: (question) => http.post('/ai/assistant', { question }, AI_TIMEOUT).then(r => r.data),
};

export const knowledgeUpdate = {
  sources: () => http.get('/knowledge-sources').then(r => r.data),
  saveSources: (body) => http.post('/knowledge-sources', body).then(r => r.data),
  runNow: () => http.post('/knowledge-update/run').then(r => r.data),
};

export const assistants = {
  list: () => http.get('/assistants').then(r => r.data),
  run: (key, message, context) => http.post(`/assistants/${key}`, { message, context }, AI_TIMEOUT).then(r => r.data),
  history: (key) => http.get(`/assistants/${key}/history`).then(r => r.data),
};

export default http;

// Customer e-signature for estimates and service agreements (backend/esign_routes.py).
export const esign = {
  create: (body) => http.post('/esign/requests', { originUrl: window.location.origin, ...body }).then(r => r.data),
  list: (params) => http.get('/esign/requests', { params: params || {} }).then(r => r.data),
  get: (id) => http.get(`/esign/requests/${id}`).then(r => r.data),
  void: (id) => http.post(`/esign/requests/${id}/void`).then(r => r.data),
};
// The books (Accounting → Journal, Ledger, Statements, Reconcile, Payroll,
// Fixed Assets, Close Books). Journal entries are derived on the server from
// invoices, payments, expenses and bills; see backend/ledger.py.
const get = (path, params) => http.get(path, { params, timeout: 60000 }).then(r => r.data);
const post = (path, body) => http.post(path, body || {}, { timeout: 60000 }).then(r => r.data);
export const ledgerApi = {
  settings: () => get('/ledger/settings'),
  saveSettings: (body) => http.put('/ledger/settings', body).then(r => r.data),
  setup: () => post('/ledger/setup'),
  health: () => get('/ledger/health'),
  entries: (params) => get('/ledger/entries', params),
  createJournal: (body) => post('/ledger/journal', body),
  voidJournal: (id) => post(`/ledger/journal/${id}/void`),
  balances: (asOf) => get('/ledger/balances', asOf ? { asOf } : undefined),
  trialBalance: (date_from, date_to) => get('/ledger/trial-balance', { date_from, date_to }),
  generalLedger: (accountId, date_from, date_to) => get('/ledger/general-ledger', { accountId, date_from, date_to }),
  profitLoss: (date_from, date_to) => get('/ledger/profit-loss', { date_from, date_to }),
  balanceSheet: (asOf) => get('/ledger/balance-sheet', { asOf }),
  cashFlow: (date_from, date_to) => get('/ledger/cash-flow', { date_from, date_to }),
  reconcile: (accountId, through) => get('/ledger/reconcile', { accountId, through }),
  saveReconcile: (body) => post('/ledger/reconcile', body),
  undoReconcile: (id) => post(`/ledger/reconcile/${id}/undo`),
  suggestions: (tid) => get(`/ledger/bank-lines/${tid}/suggestions`),
  match: (tid, kind, targetId) => post(`/ledger/bank-lines/${tid}/match`, { kind, targetId }),
  unmatch: (tid) => post(`/ledger/bank-lines/${tid}/unmatch`),
  payroll: () => get('/ledger/payroll'),
  savePayroll: (body) => post('/ledger/payroll', body),
  postPayroll: (id) => post(`/ledger/payroll/${id}/post`),
  voidPayroll: (id) => post(`/ledger/payroll/${id}/void`),
  assets: () => get('/ledger/assets'),
  saveAsset: (body) => post('/ledger/assets', body),
  runDepreciation: (throughMonth) => post('/ledger/depreciation/run', { throughMonth }),
};

/** The server's reason for refusing, for a toast. */
export const apiError = (err, fallback = 'Something went wrong') => err?.response?.data?.detail || fallback;
